import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { buildApiUrl } from '../../common/api';
import clienteAuthService from '../api/authService';
import ordiniService, { segnalazioniService, accountService, profileService } from '../api/clienteService';
import { clienteApi } from '../../common/axios';
import { downloadPdf } from '../../common/downloadPdf';
import IndirizzoForm from '../components/IndirizzoForm';
import WalletPanel from '../components/WalletPanel';
import CodiceAmicoCard from '../components/CodiceAmicoCard';
import DiventaCloserModal from '../components/DiventaCloserModal';

function euro(v) {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);
}

const STATO_LABEL = {
  in_attesa_pagamento: 'In attesa pagamento',
  nuovo: 'Confermato',
  accettato: 'Accettato',
  in_preparazione: 'In preparazione',
  spedito: 'Spedito',
  consegnato: 'Consegnato',
  annullato: 'Annullato',
};

export default function AreaRiservata() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(clienteAuthService.getCurrentUser());
  const [tab, setTab] = useState(() => searchParams.get('tab') || 'ordini');
  const [accountSub, setAccountSub] = useState('profilo');
  const [ordini, setOrdini] = useState([]);
  const [segnalazioni, setSegnalazioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [segForm, setSegForm] = useState({ oggetto: '', descrizione: '', canale: 'diletto' });
  const [segMsg, setSegMsg] = useState('');
  const [cancStato, setCancStato] = useState({ richiesta: null, blocco: null });
  const [cancForm, setCancForm] = useState({ motivo: '', password: '' });
  const [cancMsg, setCancMsg] = useState('');
  const [profilo, setProfilo] = useState({
    nome: '', cognome: '', telefono: '', indirizzo_predefinito: { via: '', citta: '', cap: '' },
    tipo_cliente: 'privato', ragione_sociale: '', codice_fiscale: '', partita_iva: '',
    codice_destinatario: '', pec: '', richiede_fattura: false,
  });
  const [profiloMsg, setProfiloMsg] = useState('');
  const [pwdForm, setPwdForm] = useState({ current_password: '', password: '', password_confirmation: '' });
  const [pwdMsg, setPwdMsg] = useState('');
  const [payMsg, setPayMsg] = useState('');
  const [showCloserModal, setShowCloserModal] = useState(false);

  const refreshOrdini = () => ordiniService.list().then((o) => setOrdini(o.data.data?.data || []));

  useEffect(() => {
    if (!clienteAuthService.isAuthenticated()) {
      navigate('/area-riservata/login');
      return;
    }
    Promise.all([
      refreshOrdini(),
      segnalazioniService.list(),
      accountService.statoCancellazione(),
      clienteApi.get('/cliente/me'),
    ])
      .then(([, s, c, me]) => {
        setSegnalazioni(s.data.data?.data || []);
        setCancStato({ richiesta: c.data.richiesta, blocco: c.data.blocco });
        const u = me.data.user;
        if (u) {
          setUser(u);
          setProfilo({
            nome: u.nome || '',
            cognome: u.cognome || '',
            telefono: u.telefono || '',
            indirizzo_predefinito: u.indirizzo_predefinito || { via: '', citta: '', cap: '' },
            tipo_cliente: u.tipo_cliente || 'privato',
            ragione_sociale: u.ragione_sociale || '',
            codice_fiscale: u.codice_fiscale || '',
            partita_iva: u.partita_iva || '',
            codice_destinatario: u.codice_destinatario || '',
            pec: u.pec || '',
            richiede_fattura: !!u.richiede_fattura,
          });
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && ['ordini', 'wallet', 'account', 'segnalazioni'].includes(t)) {
      setTab(t);
    }
    if (searchParams.get('pagamento') === 'ok') {
      setPayMsg(
        searchParams.get('credito') === '1'
          ? 'Ordine pagato con credito piattaforma!'
          : 'Pagamento completato! Se hai altri ordini in sospeso, puoi pagarli qui sotto.'
      );
    }
  }, [searchParams]);

  const logout = async () => {
    await clienteAuthService.logout();
    navigate('/area-riservata/login');
  };

  const pagaOrdine = async (ordineId) => {
    const checkoutRes = await fetch(buildApiUrl('/api/cliente/pagamenti/checkout'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${localStorage.getItem('cliente_token')}`,
      },
      body: JSON.stringify({
        ordine_id: ordineId,
        success_url: `${window.location.origin}/area-riservata?pagamento=ok`,
        cancel_url: `${window.location.origin}/area-riservata?pagamento=cancel`,
      }),
    });
    const checkout = await checkoutRes.json();
    if (checkout.pagato_con_credito) {
      setPayMsg('Ordine pagato con credito piattaforma!');
      await refreshOrdini();
      try {
        const me = await clienteApi.get('/cliente/me');
        if (me.data.user) {
          setUser(me.data.user);
          clienteAuthService.setCurrentUser(me.data.user);
        }
      } catch (_) { /* ignore */ }
      return;
    }
    if (checkout.checkout_url) window.location.href = checkout.checkout_url;
    else alert(checkout.message || 'Errore pagamento');
  };

  const richiediCancellazione = async (e) => {
    e.preventDefault();
    setCancMsg('');
    try {
      const r = await accountService.richiediCancellazione(cancForm);
      setCancStato({ richiesta: r.data.richiesta, blocco: null });
      setCancMsg(r.data.message);
      setCancForm({ motivo: '', password: '' });
    } catch (err) {
      setCancMsg(err.response?.data?.message || 'Errore invio richiesta');
    }
  };

  const salvaProfilo = async (e) => {
    e.preventDefault();
    setProfiloMsg('');
    try {
      const r = await profileService.update(profilo);
      const u = r.data.user;
      localStorage.setItem('cliente_user', JSON.stringify(u));
      setUser(u);
      setProfiloMsg('Profilo aggiornato');
    } catch (err) {
      setProfiloMsg(err.response?.data?.message || 'Errore salvataggio');
    }
  };

  const cambiaPassword = async (e) => {
    e.preventDefault();
    setPwdMsg('');
    try {
      await clienteAuthService.changePassword(pwdForm.current_password, pwdForm.password, pwdForm.password_confirmation);
      setPwdMsg('Password aggiornata');
      setPwdForm({ current_password: '', password: '', password_confirmation: '' });
    } catch (err) {
      setPwdMsg(err.response?.data?.message || 'Errore cambio password');
    }
  };

  const inviaSegnalazione = async (e) => {
    e.preventDefault();
    try {
      await segnalazioniService.create(segForm);
      setSegMsg('Segnalazione inviata. L\'admin ti risponderà a breve.');
      setSegForm({ oggetto: '', descrizione: '', canale: 'diletto' });
      const s = await segnalazioniService.list();
      setSegnalazioni(s.data.data?.data || []);
    } catch {
      setSegMsg('Errore invio segnalazione');
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FFFEFA]">
      <header className="mang-gradient text-white px-4 py-6">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold">Ciao, {user.nome}</h1>
            <p className="text-sm opacity-90">Area riservata cliente</p>
          </div>
          <div className="flex gap-3 flex-wrap justify-end">
            <Link to="/catalogo" className="text-sm underline opacity-90">Catalogo</Link>
            <Link to="/carrello" className="text-sm underline opacity-90">Carrello</Link>
            <Link to="/preferiti" className="text-sm underline opacity-90">Preferiti</Link>
            <button type="button" onClick={logout} className="text-sm underline opacity-90">Esci</button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
            {[
              { id: 'ordini', label: 'I miei Ordini' },
              { id: 'wallet', label: 'Wallet & Inviti' },
              { id: 'segnalazioni', label: 'Segnalazioni / Resi' },
              { id: 'account', label: 'Impostazioni Account' },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium ${tab === t.id ? 'bg-[#c78244] text-white' : 'bg-gray-100'}`}
              >{t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-gray-400 text-center py-12">Caricamento…</p>
        ) : tab === 'ordini' ? (
          <>
            {payMsg && <p className="text-sm bg-green-50 text-green-800 p-3 rounded-lg mb-4">{payMsg}</p>}
            {ordini.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-400 mb-4">Nessun ordine ancora</p>
                <Link to="/catalogo" className="text-[#c78244] underline">Vai al catalogo</Link>
              </div>
            ) : (
              <div className="space-y-4" data-tour="ar-ordini">
                {ordini.map((o) => (
                  <div key={o.id} className="mang-card p-5">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="font-mono text-sm text-gray-500">{o.numero_ordine}</span>
                        <h3 className="font-semibold">{o.categoria?.nome}</h3>
                      </div>
                      <span className="text-xs bg-gray-100 px-2 py-1 rounded">{STATO_LABEL[o.stato] || o.stato}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">{new Date(o.created_at).toLocaleDateString('it-IT')}</span>
                      <span className="font-bold">{euro(o.totale)}</span>
                    </div>
                    {o.stato === 'in_attesa_pagamento' && (
                      <button type="button" className="mt-3 w-full bg-[#c78244] text-white py-2 rounded-lg text-sm font-medium"
                        onClick={() => pagaOrdine(o.id)}>
                        Paga ora con Stripe
                      </button>
                    )}
                    {o.stato === 'spedito' && (
                      <button type="button" className="mt-3 w-full bg-green-600 text-white py-2 rounded-lg text-sm font-medium"
                        onClick={async () => { await ordiniService.ricevuto(o.id); await refreshOrdini(); }}>
                        ✓ Ho ricevuto il prodotto
                      </button>
                    )}
                    {o.puo_richiedere_rimborso && (
                      <button type="button" className="mt-2 w-full border border-red-400 text-red-600 py-2 rounded-lg text-sm"
                        onClick={async () => {
                          const motivo = window.prompt('Motivo della richiesta di rimborso (min 10 caratteri):');
                          if (!motivo || motivo.length < 10) return;
                          await ordiniService.richiediRimborso(o.id, motivo);
                          await refreshOrdini();
                          alert('Richiesta inviata all\'amministrazione');
                        }}>
                        Richiedi rimborso (finestra recesso)
                      </button>
                    )}
                    {o.rimborso_richiesto_at && (
                      <p className="text-xs text-amber-700 mt-2">Rimborso richiesto il {new Date(o.rimborso_richiesto_at).toLocaleString('it-IT')}</p>
                    )}
                    {o.finestra_rimborso_at && o.stato === 'consegnato' && !o.puo_richiedere_rimborso && !o.rimborso_richiesto_at && (
                      <p className="text-xs text-gray-400 mt-2">Finestra recesso scaduta</p>
                    )}
                    <div className="flex gap-2 mt-3 flex-wrap">
                      <button type="button" className="text-xs border border-[#c78244] text-[#c78244] px-3 py-1 rounded-lg"
                        onClick={() => downloadPdf(clienteApi, ordiniService.pdfOrdine(o.id), `ordine-${o.numero_ordine}.pdf`)}>
                        PDF ordine
                      </button>
                      {o.pagamento_id && (
                        <button type="button" className="text-xs border border-gray-300 px-3 py-1 rounded-lg"
                          onClick={() => downloadPdf(clienteApi, ordiniService.pdfPagamento(o.pagamento_id), `pagamento-${o.numero_ordine}.pdf`)}>
                          PDF pagamento
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : tab === 'wallet' ? (
          <div className="space-y-6">
            <CodiceAmicoCard />
            <WalletPanel />

            {/* Banner contestuale stato Closer */}
            {user.tipo_account === 'closer' ? (
              <div className="mang-card p-5 bg-gradient-to-r from-[#F6CF48]/20 to-[#c78244]/10 border border-[#c78244]/30">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🏆</span>
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-bold text-[#c78244]">Closer Mangitalia</span>
                      <span className="bg-[#c78244] text-white text-xs px-2 py-0.5 rounded-full font-semibold">Attivo</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      I tuoi guadagni da referral possono essere prelevati via bonifico quando superano la soglia minima.
                      Usa il pulsante "Richiedi Prelievo" qui sopra.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mang-card p-5 bg-[#fffdf5] border border-[#f3deb8]">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-800 mb-1">🌟 Hai del credito da spendere? Puoi anche prelevarlo.</p>
                    <p className="text-xs text-gray-500">
                      Con il programma Closer puoi convertire i crediti guadagnati con gli inviti in bonifici reali sul tuo conto.
                    </p>
                  </div>
                  <a
                    href="/diventa-closer"
                    className="shrink-0 text-sm font-semibold text-[#c78244] underline underline-offset-2 whitespace-nowrap"
                  >
                    Scopri come →
                  </a>
                </div>
              </div>
            )}

            {user.tipo_account !== 'closer' && (
              <div className="mang-card p-6 mt-6 bg-[#fff8ea] border border-[#f3deb8]">
                <h3 className="font-bold text-lg mb-2">Diventa Closer Mangitalia</h3>
                <p className="text-sm text-gray-700 mb-4">
                  Se accumuli crediti puoi decidere di spenderli sui tuoi ordini. Ma se vuoi prelevarli direttamente sul tuo conto corrente tramite bonifico, puoi convertire il tuo account in Closer.
                </p>
                <button
                  onClick={() => setShowCloserModal(true)}
                  className="bg-[#c78244] text-white px-5 py-2 rounded-xl text-sm font-medium hover:bg-[#a66a35] transition"
                >
                  Diventa Closer
                </button>
              </div>
            )}
          </div>
        ) : tab === 'segnalazioni' ? (
          <div className="grid md:grid-cols-2 gap-6">
            <form onSubmit={inviaSegnalazione} className="mang-card p-5 space-y-3">
              <h3 className="font-semibold">Nuova segnalazione</h3>
              {segMsg && <p className="text-sm text-green-700 bg-green-50 p-2 rounded">{segMsg}</p>}
              <select className="w-full border rounded-lg px-3 py-2 text-sm" value={segForm.canale}
                onChange={(e) => setSegForm({ ...segForm, canale: e.target.value })}>
                <option value="diletto">Difetto / reclamo generale</option>
                <option value="ordine">Relativa a un ordine</option>
              </select>
              <input required placeholder="Oggetto" className="w-full border rounded-lg px-3 py-2 text-sm"
                value={segForm.oggetto} onChange={(e) => setSegForm({ ...segForm, oggetto: e.target.value })} />
              <textarea required rows={4} placeholder="Descrivi il problema…" className="w-full border rounded-lg px-3 py-2 text-sm"
                value={segForm.descrizione} onChange={(e) => setSegForm({ ...segForm, descrizione: e.target.value })} />
              <button type="submit" className="w-full bg-[#c78244] text-white py-2 rounded-lg text-sm font-medium">Invia</button>
            </form>
            <div className="space-y-3">
              {segnalazioni.map((s) => (
                <div key={s.id} className="mang-card p-4 text-sm">
                  <div className="font-medium">{s.oggetto}</div>
                  <div className="text-gray-500 text-xs mt-1">{s.stato} · {new Date(s.created_at).toLocaleDateString('it-IT')}</div>
                  {s.risposta_admin && <p className="mt-2 text-green-800 bg-green-50 p-2 rounded text-xs">{s.risposta_admin}</p>}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex gap-2 flex-wrap">
              {['profilo', 'password', 'cancellazione'].map((s) => (
                <button key={s} type="button" onClick={() => setAccountSub(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium ${accountSub === s ? 'bg-[#c78244] text-white' : 'bg-gray-100'}`}>
                  {s === 'profilo' ? 'Profilo' : s === 'password' ? 'Password' : 'Cancellazione'}
                </button>
              ))}
            </div>

            {accountSub === 'profilo' && (
              <form onSubmit={salvaProfilo} className="mang-card p-5 space-y-3 max-w-lg">
                <h3 className="font-semibold">Dati personali</h3>
                {profiloMsg && <p className="text-sm text-green-700">{profiloMsg}</p>}
                <div className="grid grid-cols-2 gap-3">
                  <input required className="border rounded-lg px-3 py-2 text-sm" placeholder="Nome" value={profilo.nome}
                    onChange={(e) => setProfilo({ ...profilo, nome: e.target.value })} />
                  <input required className="border rounded-lg px-3 py-2 text-sm" placeholder="Cognome" value={profilo.cognome}
                    onChange={(e) => setProfilo({ ...profilo, cognome: e.target.value })} />
                </div>
                <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Telefono" value={profilo.telefono}
                  onChange={(e) => setProfilo({ ...profilo, telefono: e.target.value })} />
                <h4 className="text-sm font-medium pt-2">Dati fiscali</h4>
                <select className="w-full border rounded-lg px-3 py-2 text-sm" value={profilo.tipo_cliente}
                  onChange={(e) => setProfilo({ ...profilo, tipo_cliente: e.target.value })}>
                  <option value="privato">Privato</option>
                  <option value="azienda">Azienda</option>
                </select>
                {profilo.tipo_cliente === 'azienda' && (
                  <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Ragione sociale"
                    value={profilo.ragione_sociale}
                    onChange={(e) => setProfilo({ ...profilo, ragione_sociale: e.target.value })} />
                )}
                <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Codice fiscale"
                  value={profilo.codice_fiscale}
                  onChange={(e) => setProfilo({ ...profilo, codice_fiscale: e.target.value.toUpperCase() })} />
                <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Partita IVA"
                  value={profilo.partita_iva}
                  onChange={(e) => setProfilo({ ...profilo, partita_iva: e.target.value })} />
                <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Codice destinatario SDI"
                  maxLength={7} value={profilo.codice_destinatario}
                  onChange={(e) => setProfilo({ ...profilo, codice_destinatario: e.target.value })} />
                <input type="email" className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="PEC"
                  value={profilo.pec}
                  onChange={(e) => setProfilo({ ...profilo, pec: e.target.value })} />
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!profilo.richiede_fattura}
                    onChange={(e) => setProfilo({ ...profilo, richiede_fattura: e.target.checked })} />
                  Richiedo fattura elettronica quando disponibile
                </label>
                <h4 className="text-sm font-medium pt-2">Indirizzo predefinito consegna</h4>
                <IndirizzoForm
                  value={profilo.indirizzo_predefinito || {}}
                  onChange={(ind) => setProfilo({ ...profilo, indirizzo_predefinito: ind })}
                />
                <button type="submit" className="bg-[#c78244] text-white px-4 py-2 rounded-lg text-sm">Salva profilo</button>
              </form>
            )}

            {accountSub === 'password' && (
              <form onSubmit={cambiaPassword} className="mang-card p-5 space-y-3 max-w-lg">
                <h3 className="font-semibold">Cambia password</h3>
                {pwdMsg && <p className={`text-sm ${pwdMsg.includes('aggiornata') ? 'text-green-700' : 'text-red-600'}`}>{pwdMsg}</p>}
                <input type="password" required className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Password attuale"
                  value={pwdForm.current_password} onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })} />
                <input type="password" required minLength={8} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Nuova password"
                  value={pwdForm.password} onChange={(e) => setPwdForm({ ...pwdForm, password: e.target.value })} />
                <input type="password" required minLength={8} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Conferma nuova password"
                  value={pwdForm.password_confirmation} onChange={(e) => setPwdForm({ ...pwdForm, password_confirmation: e.target.value })} />
                <button type="submit" className="bg-[#c78244] text-white px-4 py-2 rounded-lg text-sm">Aggiorna password</button>
              </form>
            )}

            {accountSub === 'cancellazione' && (
              <div className="mang-card p-5 max-w-lg">
                <h3 className="font-semibold mb-2">Cancellazione account</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Puoi richiedere la cancellazione del tuo account. L&apos;amministrazione valuterà la richiesta.
                </p>
                {cancMsg && <p className="text-sm bg-green-50 text-green-800 p-2 rounded mb-3">{cancMsg}</p>}
                {cancStato.blocco && <p className="text-sm text-amber-700 bg-amber-50 p-2 rounded mb-3">{cancStato.blocco}</p>}
                {cancStato.richiesta?.stato === 'in_attesa' ? (
                  <div>
                    <p className="text-sm text-amber-800 mb-3">Richiesta in attesa dal {new Date(cancStato.richiesta.created_at).toLocaleDateString('it-IT')}.</p>
                    <button type="button" onClick={async () => { await accountService.annullaCancellazione(); setCancStato({ richiesta: null, blocco: cancStato.blocco }); setCancMsg('Richiesta annullata'); }}
                      className="text-sm text-red-600 underline">Annulla richiesta</button>
                  </div>
                ) : (
                  <form onSubmit={richiediCancellazione} className="space-y-3">
                    <textarea rows={3} placeholder="Motivo (opzionale)" className="w-full border rounded-lg px-3 py-2 text-sm"
                      value={cancForm.motivo} onChange={(e) => setCancForm({ ...cancForm, motivo: e.target.value })} />
                    <input type="password" required placeholder="Conferma con la tua password"
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      value={cancForm.password} onChange={(e) => setCancForm({ ...cancForm, password: e.target.value })} />
                    <button type="submit" disabled={!!cancStato.blocco}
                      className="w-full bg-red-600 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                      Richiedi cancellazione account
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {showCloserModal && (
        <DiventaCloserModal
          onClose={() => setShowCloserModal(false)}
          onSuccess={() => {
            setShowCloserModal(false);
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}
